import { TransportLocationTypes } from "@elit-hair/elitklinik-shared";
import { Autocomplete, Box, InputAdornment, TextField, Typography } from "@mui/material";
import { createElement, useMemo } from "react";
import { Controller, Path, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbBuildingAirport } from "react-icons/tb";
import { ZodError } from "zod";

import { transportLocationStyles } from "@/constants/transferLocations";
import { getValueByPath } from "@/helpers/object";
import { LocationSchema } from "@/pages/Plannn/components/CreateUser/CreateCustomPassengerDialog";
import { MemoizedLocationChip } from "@/pages/Plannn/components/table/PlanLocationColumn";

import { CommonLabel } from "./CommonInput";

function LocationField<T extends Record<string, unknown>>({
    location,
    loading,
    className,
    label,
    formPath,
    validLocations,
}: {
    location: LocationSchema | null;
    loading: boolean;
    className: string;
    label: string;
    formPath: Path<T>;
    validLocations: { label: string; value: string | number; type?: TransportLocationTypes }[];
}) {
    const { t } = useTranslation();

    const { control, formState } = useFormContext();
    const error = useMemo(() => getValueByPath<ZodError>(formState.errors, formPath), [formState]);

    const getTypeLabel = (type: TransportLocationTypes) => {
        const typeStyle = transportLocationStyles.find((style) => style.value === type);
        return typeStyle?.label || "";
    };

    return (
        <CommonLabel label={t(label)} className={className}>
            <Controller
                name={formPath}
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        {...field}
                        classes={{
                            inputRoot: " !h-[47px]",
                        }}
                        sx={() => ({
                            "label + &": {
                                marginTop: 20,
                            },
                            "& .MuiInputBase-input": {
                                paddingBottom: 12,
                                paddingTop: 12,
                            },
                        })}
                        value={location || null}
                        options={validLocations}
                        disabled={loading}
                        groupBy={(option) => getTypeLabel(option.type as TransportLocationTypes)}
                        renderGroup={(params) => {
                            const type = transportLocationStyles.find((style) => style.label === params.group);
                            return (
                                <Box key={params.key}>
                                    <Box
                                        className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200"
                                        style={{ backgroundColor: type?.bg }}
                                    >
                                        {type &&
                                            createElement(type.component(), {
                                                className: "w-4 h-4",
                                                style: { color: type.text },
                                            })}
                                        <Typography
                                            variant="body2"
                                            className="font-medium"
                                            style={{ color: type?.text }}
                                        >
                                            {params.group}
                                        </Typography>
                                    </Box>
                                    <Box>{params.children}</Box>
                                </Box>
                            );
                        }}
                        onChange={(_, value) => {
                            field.onChange(value || null);
                        }}
                        getOptionLabel={(val) => {
                            return t(val?.label || "");
                        }}
                        renderOption={(props, val) => {
                            const { key, ...optionProps } = props;

                            return (
                                <Box
                                    key={key}
                                    component="li"
                                    {...optionProps}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        padding: 0,
                                        gap: 0,
                                    }}
                                >
                                    <MemoizedLocationChip value={String(val?.value || "")} />
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={t("placeholders.select", { field: t("location") })}
                                error={!!error}
                                helperText={error && error?.message}
                                variant="filled"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        transform: "translateY(-8px)",
                                    },
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="end" className="  !-translate-x-1 !-translate-y-2">
                                            <TbBuildingAirport />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            />
        </CommonLabel>
    );
}

export default LocationField;
